import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchPost } from '@app/src/api/fetchHooks'
import { useAccount } from '@app/src/context/AccountContext'
import { useResponseItemContext } from '@app/src/context/ResponseItemContext'
import useFetchSuggestionFiles from '@app/src/pages/Questionnaire/hooks/useFetchSuggestionFiles'
import { useReport } from '@app/src/pages/Questionnaire/ReportContext'
import type { Collection, RequestItem } from '@app/src/types/resourceExplorer'
import { useEffect, useState } from 'react'

export interface AiSuggestion {
  response: string
  selectedOptions?: string[]
  citations: string[]
  cannotAnswer?: boolean
}

type UseFetchAiSuggestions = {
  fetchedData?: AiSuggestion
  isLoadingFileAiSuggestions: boolean
}

type PayloadType = {
  organizationName?: string
  periodName?: string
  requestItem: {
    id: number
    description: string
    allowMultiChoice?: boolean
    unit: { symbol?: string | null }
    requestCollection: {
      id: number
      items: Collection[]
    }
    isRequired: boolean
  }
}

const useFetchFileAiSuggestions = (
  requestItem: RequestItem,
  questionType: string,
  isPreview: boolean,
  enabled: boolean,
  periodName?: string,
): UseFetchAiSuggestions => {
  const { account } = useAccount()
  const { isResponseFetching, response } = useReport()
  const { hideInputField } = useResponseItemContext()
  const [shouldShowSuggestion, setShouldShowSuggestion] = useState(false)

  const {
    files,
    isLoadingFiles,
    hasSuggestedAnswersFeature: suggestedAnswers,
  } = useFetchSuggestionFiles(enabled && !isPreview)

  useEffect(() => {
    if (isLoadingFiles || !files?.length) return
    if (!suggestedAnswers) return
    if (isResponseFetching) return
    if (hideInputField) return

    const responseItem = response?.items?.find(it => it.requestItemId === requestItem.id)
    const output = Boolean(responseItem && !responseItem.isAnswered)

    setShouldShowSuggestion(output)
  }, [isLoadingFiles, files, suggestedAnswers, isResponseFetching, hideInputField, response, requestItem.id])

  const {
    data: fetchedData,
    isLoading,
    isRefetching,
  } = useFetchPost<AiSuggestion, PayloadType>({
    key: [FetchKey.SuggestedResponse, requestItem.id],
    endpoint: endpoints.suggestedResponse(questionType?.toLowerCase()),
    payload: {
      organizationName: account?.organization?.name,
      periodName: periodName,
      requestItem: {
        id: requestItem.id,
        requestCollection: requestItem.requestCollection,
        allowMultiChoice: requestItem.allowMultiChoice,
        unit: requestItem.unit,
        description: requestItem.description,
        isRequired: requestItem.isRequired,
      },
    },
    shouldShowErrorNotification: false,
    options: {
      enabled: enabled && shouldShowSuggestion && Boolean(requestItem.description) && !isPreview,
      retry: false,
      refetchOnMount: false,
      staleTime: Infinity,
      gcTime: Infinity,
    },
  })
  return { fetchedData, isLoadingFileAiSuggestions: isLoading || isRefetching }
}

export default useFetchFileAiSuggestions
